import type { SeasonDefinition, SeasonSetting } from './types';
import { HALLOWEEN_2026 } from './seasons/halloween-2026';

// Every season the site knows. Order does not matter; windows must not
// overlap. Adding a season is a new file in ./seasons plus a line here (and,
// for a new look, its layer in ./looks.ts).
export const SEASONS: readonly SeasonDefinition[] = [HALLOWEEN_2026];

function inWindow(season: SeasonDefinition, now: number): boolean {
  const start = Date.parse(season.window.start);
  const end = Date.parse(season.window.end);
  return Number.isFinite(start) && Number.isFinite(end) && now >= start && now < end;
}

/** The season whose window contains `now`, if any. */
export function seasonForDate(now: number = Date.now()): SeasonDefinition | null {
  return SEASONS.find(s => inWindow(s, now)) ?? null;
}

/**
 * Looks a season up by id, or by look (the current one for that look if its
 * window is open, otherwise the most recent).
 */
export function findSeason(key: string, now: number = Date.now()): SeasonDefinition | null {
  const k = key.trim().toLowerCase();
  const byId = SEASONS.find(s => s.id === k);
  if (byId) return byId;
  const byLook = SEASONS.filter(s => s.look === k);
  if (byLook.length === 0) return null;
  return (
    byLook.find(s => inWindow(s, now)) ??
    [...byLook].sort((a, b) => Date.parse(b.window.start) - Date.parse(a.window.start))[0]
  );
}

/**
 * Resolves a setting to a season. 'off' (or 'none') is no season; a known id
 * or look forces it regardless of date; 'auto', empty, or an unknown value
 * follows the date windows.
 */
export function resolveSeason(
  setting: SeasonSetting | null | undefined,
  now: number = Date.now(),
): SeasonDefinition | null {
  const s = (setting ?? '').trim().toLowerCase();
  if (s === 'off' || s === 'none') return null;
  if (s && s !== 'auto') {
    const forced = findSeason(s, now);
    if (forced) return forced;
  }
  return seasonForDate(now);
}
